import React, { useEffect, useRef } from 'react';
import { FieldGuide, Label, TextInputLarge } from '../../components/forms/input';
import * as Button from '../../components/button/button';
import * as Page from '../../components/content/page-header';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { usePostMutation, usePutMutation } from '../../hooks/use-mutations';
import { Select2 } from '../../components/forms/select';
import { Pageable, usePagination } from '../../hooks/use-pagination';
import { Icons } from '../../components/icons/icons';
import { useQuery } from '../../hooks/use-query';
import { MapServer } from './types/map-server';
import tenantService from '../../services/tenant-service';
import { Checkbox2 } from '../../components/forms/checkbox';
import { CordSystem } from '../settings/types/cordinate-system';
import { Link } from 'react-router-dom';

export const MapServiceEditor = (): JSX.Element => {
  const saveRef = useRef<HTMLButtonElement>();
  const { register, handleSubmit, setValue, formState: { errors } } = useForm();
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id as string;
  const postMutation = usePostMutation({ path: '/map-services' });
  const putMutation = usePutMutation({ path: `/map-services/${id}` });
  const mapServers = usePagination<Pageable<MapServer>>('/map-servers', { size: 9999 });
  const cordSystems = usePagination<Pageable<CordSystem>>('/cord-systems', { size: 9999 });
  const mapServer = useQuery<MapServer>({ path: `/map-services/${id}`, options: { enabled: id !== 'new' } });

  useEffect(() => {
    if (mapServer.data) {
      setUpdateValues();
    }
  }, [mapServer.isSuccess]);

  useEffect(() => {
    if (postMutation.isSuccess || putMutation.isSuccess) {
      navigate('/map-services');
    }
  }, [postMutation.isSuccess, putMutation.isSuccess]);

  const onSubmit = (fields: FieldValues): void => {
    id === 'new' ? postMutation.mutate(fields) : putMutation.mutate(fields);
  };

  const setUpdateValues = (): void => {
    for (const key in mapServer.data) {
      if (key === 'id') {
        continue;
      }
      setValue(key, mapServer.data[key as keyof MapServer]);
    }
  };

  return <div>
    <Page.Header>
      <Page.Tenant />
      <Page.Title>{id === 'new' ? 'Add' : 'Edit'} Map Service</Page.Title>
      <Page.SubTitle>Add a Map Service to the system that is not available in the Gold Master.</Page.SubTitle>
    </Page.Header>
    <div className="row">
      <div className="col-9 bg-light left-col">
        <form id="static-translation-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <Label name="slug">Map Service Id*</Label>
            <TextInputLarge {...register('slug', { required: 'You must provide a value for Map Service Id.' })} errors={errors} placeholder="Map Service Id" />
            <FieldGuide>Name of the Map Service.</FieldGuide>
          </div>
          <div className="mb-4">
            <Label name="location">Location</Label>
            <TextInputLarge {...register('location')} placeholder="Location" />
            <FieldGuide>Path to the Map Service.</FieldGuide>
          </div>
          <div className="mb-4">
            <Label name="mapServerId">Map Server</Label>
            <Select2 {...register('mapServerId', { required: 'You must provide a value for Map Server.' })} errors={errors}>
              <option value="">Select Map Server</option>
              {mapServers.data?.content?.map(server => {
                return <option key={server.id} value={server.id}>{server.name}</option>;
              })}
            </Select2>
            <FieldGuide>Map Server hosting the Map Service.</FieldGuide>
          </div>
          <div className="mb-4">
            <Label name="cordSysId">Coordinate System Id</Label>
            <Select2 {...register('cordSysId', { required: 'You must provide a value for Coordinate System Id.' })} errors={errors}>
              <option value="">Select Coordinate System</option>
              {cordSystems.data?.content?.map(cordSystem => {
                return <option key={cordSystem.id} value={cordSystem.id}>{cordSystem.coordSysId}</option>;
              })}
            </Select2>
            <FieldGuide>Protocol used for map server.</FieldGuide>
          </div>
          {tenantService.isGoldMaster() && (
            <div className="mb-4">
              <Checkbox2 {...register('locked')} />
            </div>
          )}
          {id !== 'new' && (
            <>
              <div className="mb-4">
                <Link to="/map-layers" className="primary-link">
                  <Icons.Link /> Map Service URL
                </Link>
              </div>
              <div>
                <Link to={`/map-layers?mapServiceId=${id}`} className="primary-link">
                  <Icons.Link /> View Layers
                </Link>
              </div>
            </>
          )}
        </form>
      </div>
      <div className="col-3 d-flex flex-column">
        <div className="px-5">
          <div className="mb-3">
            <Button.Primary form="static-translation-form" type="submit" ref={saveRef} disabled={postMutation.isLoading || putMutation.isLoading}>
              {id === 'new' ? <><Icons.Add />Add</>: 'Update'}
            </Button.Primary>
          </div>
          <div className="mb-3">
            <Button.Tertiary onClick={(): void => navigate('/map-services')} disabled={postMutation.isLoading || putMutation.isLoading}>
              Cancel
            </Button.Tertiary>
          </div>
        </div>
      </div>
    </div>
  </div>;
};
